import { fold } from "@indietabletop/appkit";
import { createContext, use, useCallback, useMemo } from "react";
import { useInvokeClient } from "../../client.ts";
import { useQuery } from "../../database.ts";
import { Gang } from "../../engine/Gang.ts";
import { useResolveRuleset } from "../rulesets.tsx";

export const GangContext = createContext<null | {
  gang: Gang;
  readOnly?: boolean;
}>(null);

export function useGangContext() {
  const context = use(GangContext);

  if (!context) {
    throw new Error(`Gang not found within context.`);
  }

  return context;
}

/**
 * Returns a result of gang based on data from IDB and ruleset referenced
 * by target gang.
 */
export function useGang(gangId: string) {
  // Get gang data from IDB
  const data = useQuery(
    useCallback(async (db) => await db.gangs.getOne(gangId), [gangId]),
    `gang:${gangId}`
  );

  // Resolve ruleset once gang data is available
  const ruleset = useResolveRuleset(data.valueOrNull()?.rulesetVersion);

  // Instantiate gang using gang data and comatible ruleset
  return useMemo(
    () => fold([ruleset, data]).mapSuccess((args) => new Gang(...args)),
    [ruleset, data]
  );
}

/**
 * Returns a result of gang based on data from IDB and ruleset referenced
 * by target gang.
 */
export function useSharedGang(snapshotId: string) {
  const [sharedGangResult] = useInvokeClient("getSharedGang", [snapshotId]);

  // Resolve ruleset once gang data is available
  const rulesetResult = useResolveRuleset(
    sharedGangResult.valueOrNull()?.rulesetVersion
  );

  // Instantiate gang using gang data and compatible ruleset
  return useMemo(
    () =>
      fold([rulesetResult, sharedGangResult]).mapSuccess(
        ([ruleset, data]) =>
          new Gang(ruleset, { ...data, deleted: false, snapshotId: null })
      ),
    [rulesetResult, sharedGangResult]
  );
}
